import React, { useMemo, useState } from 'react';
import { Colors } from '../../../../styles/theme';
import FormLabel from '../../Common/FormLabel';
import FormHelper from '../../Common/FormHelper';
import styled from 'styled-components';
import { JRCFormFieldRendererProps } from './JRCFormFieldRenderer.types';
import { useController, FieldValues } from 'react-hook-form';
import { JRCTypography } from '../../../JRCTypography/JRCTypography';
import { JRCFormErrors } from './JRCFormErrors';
import { useFormControls } from './useFormControls';
import { useIntl } from 'react-intl';
import { StyledDescription } from '../Deprecated/JRCFormFieldRender';
import { formRuleMaxLength } from '../../../../utils/utils.form';

const StyledFieldWrapper = styled.fieldset<{ margin?: string; width?: string }>`
    margin: ${(props) => (props.margin !== undefined ? props.margin : '12px 0px')} !important;
    ${(props) => props?.width && `width: ${props.width} !important;`}
`;

const Required = styled(JRCTypography).attrs(() => ({ size: 's' }))<{ error: boolean }>`
    color: ${(props) => (props.error ? props.theme.color.orange : props.theme.color.grey4)};
    transition: color 0.4s;
`;

const LabelWrapper = styled.div`
    display: flex;
    gap: 10px;
    justify-content: space-between;
    align-items: end;
    width: 100%;
    padding-right: 5px;
`;
const MaxLengthIndicator = styled.div`
    display: flex;
    justify-content: flex-end;
    font-size: 12px;
`;

export function JRCFormFieldRenderer<TFieldValues extends FieldValues = FieldValues>(
    props: JRCFormFieldRendererProps<TFieldValues>,
) {
    const intl = useIntl();
    const {
        field,
        fieldState: { error },
    } = useController({
        name: props.name,
        control: props.control,
        rules: Object.assign(useFormControls(props.rules, intl) || {}, { validate: props.rulesValidate } || {}),
    });

    const [helperColor, setHelperColor] = useState<Colors>('grey3');
    const colorHelperIcon = (event: boolean) => setHelperColor(event ? 'yellow' : 'grey3');

    const maxLength = useMemo(() => formRuleMaxLength(props.rules?.maxLength), [props.rules?.maxLength]);

    function isRequiredInvalid() {
        return !!(
            props.rules?.required &&
            ((error && error.types && Object.keys(error.types).includes('required')) ||
                field.value === undefined ||
                field.value === null ||
                field.value === '' ||
                (Array.isArray(field.value) && !field.value.length))
        );
    }
    return (
        <StyledFieldWrapper className={props.className} margin={props.margin} width={props.width}>
            {!props.noLabel &&
                (props.label || props.helper || props.description || (props.rules?.required && !props.readOnly)) && (
                    <FormLabel
                        htmlFor={props.htmlFor || props.name}
                        labelIsLegend={props.labelIsLegend}
                        sideMargin={props.labelMargin}>
                        <LabelWrapper>
                            <div>
                                {props.label && intl.formatMessage({ id: props.label })}
                                {props.helper && <FormHelper tooltip={props.helper} color={helperColor} />}
                                {props.description && (
                                    <StyledDescription>
                                        {typeof props.description === 'string'
                                            ? intl.formatMessage({ id: props.description })
                                            : props.description}
                                    </StyledDescription>
                                )}
                            </div>
                            {props.rules?.required && !props.readOnly && (
                                <Required error={isRequiredInvalid()}>
                                    {intl.formatMessage({ id: 'GLOBAL_Form_Controls_Required' })}
                                </Required>
                            )}
                        </LabelWrapper>
                    </FormLabel>
                )}
            <div className="input-wrapper" onFocus={() => colorHelperIcon(true)} onBlur={() => colorHelperIcon(false)}>
                {props.renderFunction(field)}
            </div>
            {maxLength && field.value?.length >= 0 && (
                <MaxLengthIndicator>
                    {field.value.length} / {maxLength}
                </MaxLengthIndicator>
            )}
            <JRCFormErrors error={error} />
        </StyledFieldWrapper>
    );
}
